import usePeriodicRender from '@/hooks/periodicRender'
import { secondsToHumanReadable } from '@/utils/datetime'
import { Button, px, useMantineColorScheme } from '@mantine/core'
import { differenceInSeconds, isAfter } from 'date-fns'
import { head } from 'lodash-es'
import { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { FaPlay } from 'react-icons/fa'
import AlertContainer from './AlertContainer'

const NextRouteAlert = (params?: { routeId: string; startAfter: Date; streetName?: string; onStart: () => void }) => {
  const now = usePeriodicRender(1000)
  const colorScheme = useMantineColorScheme()
  const startThresholdDiff = params ? differenceInSeconds(params?.startAfter, now) : 0
  const canStart = params && isAfter(now, params.startAfter)

  const timeToStart = secondsToHumanReadable(startThresholdDiff)
  const streetName = params?.streetName ?? '-'
  const routeId = head((params?.routeId ?? '').split(':'))!

  const notification = useMemo(
    () => ({
      message:
        params === undefined ? (
          ''
        ) : canStart ? (
          <Trans ns="navigation" i18nKey="driveToStart" streetName={streetName} routeId={routeId}>
            Drive to the start of the route <strong>{{ routeId } as unknown as React.ReactNode}</strong> (
            <strong>{{ streetName } as unknown as React.ReactNode}</strong> St.) and press
            <Button
              variant="light"
              leftSection={<FaPlay />}
              color={colorScheme.colorScheme === 'dark' ? 'white' : 'black'}
              onClick={params.onStart}
              styles={{
                label: { fontSize: px(32) },
                root: {
                  height: px(45),
                  paddingLeft: px(16),
                  paddingRight: px(16),
                  borderColor: 'black',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderRadius: 10,
                },
                section: {
                  icon: {
                    fontSize: 32,
                  },
                },
              }}
            >
              Start
            </Button>
          </Trans>
        ) : (
          <Trans
            ns="navigation"
            i18nKey="driveToStartAndWait"
            streetName={streetName}
            routeId={routeId}
            timeToStart={timeToStart}
          >
            Drive to the start of the route <strong>{{ routeId } as unknown as React.ReactNode}</strong> (
            <strong>{{ streetName } as unknown as React.ReactNode}</strong> St.) and wait{' '}
            <strong>{{ timeToStart } as unknown as React.ReactNode}</strong>
          </Trans>
        ),
    }),
    [streetName, routeId, timeToStart, canStart, params, colorScheme.colorScheme]
  )

  return (
    <AlertContainer
      type="alert"
      alertText={notification.message}
      alertColor="teal"
      position="bottom"
      opacity="medium"
      noOverlay
      textSize="var(--spacing-lg)"
      height="small"
    />
  )
}

export default NextRouteAlert
